import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiCheck, FiAlertTriangle, FiEdit2 } from "react-icons/fi";
import axios from "../../../utils/api/axios";
import { toast } from "react-hot-toast";

interface EditTargetModalProps {
    target: {
        target_id: string;
        target_name: string;
        target_value: string;
        environment: string;
        target_type: string;
    };
    assetId: string;
    onClose: () => void;
    onSuccess: () => void;
}

const EditTargetModal: React.FC<EditTargetModalProps> = ({
    target,
    assetId,
    onClose,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        target_name: target.target_name,
        target_value: target.target_value,
        environment: target.environment,
    });
    const [loading, setLoading] = useState(false);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.put(`/assets/${assetId}/targets/${target.target_id}`, formData);
            toast.success("Target updated successfully!");
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("Error updating target:", error);
            toast.error(error.response?.data?.message || "Failed to update target.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-[#0B0F19]/60 backdrop-blur-sm">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col border border-slate-200"
            >
                <div className="bg-[#1a1f37] px-6 py-4 flex justify-between items-center text-white">
                    <div className="flex items-center gap-2">
                        <FiEdit2 className="w-5 h-5 text-blue-400" />
                        <h2 className="text-lg font-bold">Edit Target</h2>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg transition-colors">
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase">Target Name</label>
                        <input
                            type="text"
                            required
                            value={formData.target_name}
                            onChange={(e) => handleInputChange("target_name", e.target.value)}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-medium text-sm"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase">Target Value</label>
                        <input
                            type="text"
                            required
                            value={formData.target_value}
                            onChange={(e) => handleInputChange("target_value", e.target.value)}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase">Environment</label>
                        <select
                            value={formData.environment}
                            onChange={(e) => handleInputChange("environment", e.target.value)}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-medium text-sm"
                        >
                            <option>Production</option>
                            <option>Staging</option>
                            <option>UAT</option>
                            <option>Development</option>
                        </select>
                    </div>

                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <><FiCheck className="w-4 h-4" /> Save Changes</>
                            )}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default EditTargetModal;
